import dynamic from 'next/dynamic';

import { CareersDecorator } from '@natu/ui/Decorators/DecoratorWrapper';
import { cn } from '@natu/utils/cn';

import { type StoryblokRichTextData } from '../../../../richtext';
import {
  getAssetFromStoryblok,
  resolveStoryblokStyles,
  sbEditable,
  type SBProps,
  type Spacing,
  type StoryblokAsset,
} from '../../../../utils';

const Rating = dynamic(() => import('@natu/ui/Rating').then(mod => mod.Rating));

const ResponsiveImage = dynamic(() =>
  import('@natu/ui/ResponsiveImage').then(mod => mod.ResponsiveImage),
);

const StoryblokRichtext = dynamic(() =>
  import('../../../../richtext').then(mod => mod.StoryblokRichtext),
);

interface SBOurGuaranteeSectionProps {
  priority?: boolean;
  image?: StoryblokAsset;
  rating?: string;
  content?: StoryblokRichTextData;
  ceos?: string;
  position?: string;
  mbMobile?: Spacing;
  mbTablet?: Spacing;
  mbDesktop?: Spacing;
  mtMobile?: Spacing;
  mtTablet?: Spacing;
  mtDesktop?: Spacing;
}

export const SBOurGuaranteeSection = ({ blok }: SBProps<SBOurGuaranteeSectionProps>) => {
  const {
    priority,
    image,
    rating,
    content,
    ceos,
    position,
    mbMobile,
    mbTablet,
    mbDesktop,
    mtMobile,
    mtTablet,
    mtDesktop,
  } = blok;

  const className = resolveStoryblokStyles({
    mt: mtMobile,
    mtTablet,
    mtDesktop,
    mb: mbMobile,
    mbTablet,
    mbDesktop,
  });

  const resolvedImage = getAssetFromStoryblok(image);

  return (
    <div
      className={cn(
        className,
        'md:grid md:grid-cols-[1fr_2fr] md:items-center xl:grid-cols-[350px_auto]',
      )}
      {...sbEditable(blok)}
    >
      {resolvedImage.src && (
        <ResponsiveImage
          {...resolvedImage}
          priority={priority}
          className="mx-auto mb-8 aspect-square rounded-3xl object-cover md:mx-0 md:mb-0"
        />
      )}
      <div className="relative flex w-full items-center justify-center self-stretch xl:ml-16">
        <div className="hidden xl:block">
          <CareersDecorator />
        </div>
        <div className="max-w-[350px] md:flex md:max-w-[440px] md:flex-col md:justify-between md:gap-y-7 md:self-stretch md:pl-8 lg:max-w-[485px] lg:gap-y-16 xl:mx-auto xl:pl-0">
          {rating && (
            <Rating
              count={5}
              value={rating || '5.0'}
              className="mx-auto mb-8 w-fit md:mx-0 md:mb-0"
            />
          )}
          {content && (
            <StoryblokRichtext
              data={content}
              className="mb-8 text-center md:mb-0 md:text-left [&>*]:m-0"
            />
          )}
          {ceos && position && (
            <div className="flex flex-col gap-y-1 text-center md:gap-y-0 md:text-left">
              <p className="text-lg font-semibold text-black md:text-base xl:text-xl">{ceos}</p>
              <span className="xl:text-md text-base text-gray-600 md:text-sm">{position}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
